// janus-engine/actions/roll-source.ts — the ONE randomness seam for every client-truth rolled action.
//
// ⚠ RNG OWNERSHIP (owner Q1-REVISED, 2026-09-07): the BROWSER rolls and the server RECORDS. There is no
//   server-issued draw stream, no cursor and no range verification — the owner set the anti-cheat
//   premise aside for now, with an audit system named as a possible later addition. This interface is
//   therefore the whole RNG contract: keep every draw going through it so an audit, a seeded source, or
//   a server-issued one can replace it later without touching a single resolver.
//
// ⚠ PHP does NOT have one dice seam, and neither can we. One MELEE attack reaches four random sites —
//   roll_dice_arr (the d20 and the damage dice), skill_roll_inc.php:1142 (the trained-tier die, in
//   EVERY skill roll), exploding_dice.php:103 (the explosion chain) and crit_variants.php (crit dice at
//   :303 AND a uniform FRACTION at :226). A ranged attack adds three more. `fraction()` exists because
//   of that last one: `rand() / getrandmax()` is a probability, not a face, and a source that only
//   offered die faces could not express it.

export interface RollSource {
  /** A face of a `die`-sided die: an integer in 1..die. */
  d: (die: number) => number;
  /** A uniform fraction in [0, 1] — PHP's `rand() / getrandmax()`. ⚠ The endpoint is INCLUSIVE: PHP can
   *  return `getrandmax()` and hence exactly 1.0. Used for the Crit: Rolled budget remainder, which is a
   *  probability rather than a die roll. (A `fraction < remainder` test treats 1.0 as "do not bump",
   *  which is the same thing PHP does with it.) */
  fraction: () => number;
}

/** A recording wrapper: keeps every draw in the order it was asked for, which is what the commit sends. */
export type RecordedDraw = { kind: 'die'; die: number; value: number } | { kind: 'fraction'; value: number };

export function recordingRollSource(inner: RollSource): RollSource & { draws: RecordedDraw[] } {
  const draws: RecordedDraw[] = [];
  return {
    draws,
    d(die: number): number {
      const value = inner.d(die);
      draws.push({ kind: 'die', die, value });
      return value;
    },
    fraction(): number {
      const value = inner.fraction();
      draws.push({ kind: 'fraction', value });
      return value;
    },
  };
}

/** Pause for an actual-outcome prompt, then replay the exact consumed prefix before drawing more.
 * A changed die fails through the strict replay seam; a prompt never rerolls the primary action. */
export function resumableRollSource(inner: RollSource): { next: () => RollSource & { remaining: () => number }; draws: RecordedDraw[] } {
  const draws: RecordedDraw[] = [];
  return { draws, next: () => {
    const prefix = replayRollSource([...draws]);
    return {
      d: die => {
        if (prefix.remaining()) return prefix.d(die);
        const value = inner.d(die); draws.push({ kind: 'die', die, value }); return value;
      },
      fraction: () => {
        if (prefix.remaining()) return prefix.fraction();
        const value = inner.fraction(); draws.push({ kind: 'fraction', value }); return value;
      },
      remaining: prefix.remaining,
    };
  } };
}

/**
 * Replays a recorded sequence — the differential-replay source. Throws rather than inventing a draw, so
 * a resolver that asks for MORE than PHP did fails loudly instead of silently diverging, and one that
 * asks for a FRACTION where PHP took a die face (or the reverse) is caught rather than absorbed.
 */
export function replayRollSource(draws: ReadonlyArray<RecordedDraw | number>): RollSource & { remaining: () => number } {
  let index = 0;
  const next = (): RecordedDraw => {
    if (index >= draws.length) throw new Error(`replay exhausted after ${draws.length} draws`);
    const entry = draws[index++];
    if (entry === undefined) throw new Error(`replay draw ${index} is missing`);
    return typeof entry === 'number' ? { kind: 'die', die: NaN, value: entry } : entry;
  };
  return {
    d(die: number): number {
      const entry = next();
      if (entry.kind !== 'die') throw new Error(`replay draw ${index} is a fraction, but a d${die} was asked for`);
      if (Number.isFinite(entry.die) && entry.die !== die) {
        throw new Error(`replay draw ${index} is a d${entry.die}, but a d${die} was asked for`);
      }
      if (!Number.isInteger(entry.value) || entry.value < 1 || entry.value > die) {
        throw new Error(`replay draw ${index} (${entry.value}) is not a face of a d${die}`);
      }
      return entry.value;
    },
    fraction(): number {
      const entry = next();
      if (entry.kind !== 'fraction') throw new Error(`replay draw ${index} is a die face, but a fraction was asked for`);
      // INCLUSIVE upper bound: `rand() / getrandmax()` is exactly 1.0 whenever rand() returns its max,
      // so rejecting 1.0 would refuse a legitimate recorded draw (Codex review 2026-09-07).
      if (!(entry.value >= 0 && entry.value <= 1)) throw new Error(`replay draw ${index} (${entry.value}) is not a fraction in [0, 1]`);
      return entry.value;
    },
    /** How many recorded draws have NOT been consumed. Exhaustion catches a resolver that draws too
     *  MANY; this catches one that draws too FEW — an omitted explosion link would otherwise replay
     *  clean (Codex review 2026-09-07). Assert it is 0 after resolving a supported action. */
    remaining(): number {
      return draws.length - index;
    },
  };
}

/**
 * A TAPE first, then `inner`, POSITION BY POSITION: draw i replays tape entry i when it asks for the same kind and die; a draw that now asks
 * for something else (a different die — e.g. a trained-tier die the changed state resized) takes a fresh draw IN PLACE of that one entry,
 * and every later position still replays its own entry (Codex review #4 F5: one changed die must not re-roll the unrelated draws after it).
 * Past the tape's end every draw is fresh. For re-resolving an already-drawn verdict on changed state (D-051, review #3 F2).
 */
export function tapedRollSource(tape: ReadonlyArray<RecordedDraw>, inner: RollSource): RollSource & { fresh: () => boolean } {
  let index = 0, live = false;
  return {
    d(die: number): number {
      const entry = tape[index++];
      if (entry && entry.kind === 'die' && entry.die === die) return entry.value;
      live = true;
      return inner.d(die);
    },
    fraction(): number {
      const entry = tape[index++];
      if (entry && entry.kind === 'fraction') return entry.value;
      live = true;
      return inner.fraction();
    },
    /** Did any draw come from `inner` (the tape did not cover the re-resolution)? */
    fresh: () => live,
  };
}

/**
 * The live browser roller. `crypto.getRandomValues` with rejection sampling, so every face is equally
 * likely — `Math.floor(Math.random() * die)` is biased for any die that does not divide 2^32, and while
 * the bias is tiny it costs nothing to avoid and makes the source auditable later.
 */
export function browserRollSource(): RollSource {
  const words = (): number => {
    const buffer = new Uint32Array(1);
    // The engine's tsconfig has no DOM lib; the browser (and Node 19+) both expose WebCrypto here.
    (globalThis as unknown as { crypto: { getRandomValues(array: Uint32Array): void } }).crypto.getRandomValues(buffer);
    return buffer[0] ?? 0;
  };
  return {
    d(die: number): number {
      if (!Number.isInteger(die) || die < 1) throw new Error(`not a die: ${die}`);
      if (die === 1) return 1;
      const limit = Math.floor(0x1_0000_0000 / die) * die;
      let x = words();
      while (x >= limit) x = words(); // reject the biased tail
      return 1 + (x % die);
    },
    fraction(): number {
      return words() / 0x1_0000_0000;
    },
  };
}
